import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Brick } from '../../services/number-rule.service';

interface BricksEditorProps {
    bricks: Brick[];
    onChange: (bricks: Brick[]) => void;
}

const BRICK_TYPES = [
    { type: 'text', label: '固定文本', icon: 'title', defaultVal: 'WA' },
    { type: 'date', label: '日期', icon: 'calendar_today', defaultVal: 'YYYYMMDD' },
    { type: 'sequence', label: '递增流水', icon: 'tag', defaultVal: 4 },
    { type: 'random', label: '随机字母', icon: 'casino', defaultVal: 4 },
    { type: 'separator', label: '分隔符', icon: 'horizontal_rule', defaultVal: '-' },
    { type: 'staff', label: '员工代码', icon: 'person', defaultVal: '' },
    { type: 'company', label: '公司代码', icon: 'business', defaultVal: '' },
];

const BricksEditor: React.FC<BricksEditorProps> = ({ bricks, onChange }) => {
    const { t } = useTranslation();
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const addBrick = (typeInfo: typeof BRICK_TYPES[0]) => {
        if (typeInfo.type === 'sequence' && bricks.some(b => b.type === 'sequence')) {
            return; // Only one sequence allowed
        }

        const newBrick: Brick = { type: typeInfo.type as any };
        if (typeInfo.type === 'text' || typeInfo.type === 'separator') newBrick.value = typeInfo.defaultVal as string;
        if (typeInfo.type === 'date') newBrick.format = typeInfo.defaultVal as string;
        if (typeInfo.type === 'sequence' || typeInfo.type === 'random') newBrick.length = typeInfo.defaultVal as number;

        onChange([...bricks, newBrick]);
    };

    const removeBrick = (index: number) => {
        const newBricks = [...bricks];
        newBricks.splice(index, 1);
        onChange(newBricks);
    };

    const updateBrick = (index: number, updates: Partial<Brick>) => {
        const newBricks = [...bricks];
        newBricks[index] = { ...newBricks[index], ...updates };
        onChange(newBricks);
    };

    const moveBrick = (from: number, to: number) => {
        const newBricks = [...bricks];
        const [moved] = newBricks.splice(from, 1);
        newBricks.splice(to, 0, moved);
        onChange(newBricks);
        setDraggedIndex(null);
    };

    return (
        <div className="space-y-6">
            {/* Library / Palette */}
            <div>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2 block">
                    {t('settings.bricks_library', '可用积木块 (点击添加)')}
                </label>
                <div className="flex flex-wrap gap-2">
                    {BRICK_TYPES.map(bt => {
                        const isDisabled = bt.type === 'sequence' && bricks.some(b => b.type === 'sequence');
                        return (
                            <button
                                key={bt.type}
                                onClick={() => addBrick(bt)}
                                disabled={isDisabled}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all active:scale-95 ${isDisabled
                                        ? 'bg-slate-800 border-slate-700 text-slate-600 opacity-50 cursor-not-allowed'
                                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-primary/20 hover:border-primary/30 hover:text-primary'
                                    }`}
                            >
                                <span className="material-icons text-sm">{bt.icon}</span>
                                {bt.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Workbench / Stack */}
            <div className="bg-black/20 rounded-2xl p-4 min-h-[120px] border border-white/5 relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-3 block text-center">
                    {t('settings.bricks_workbench', '拖拽积木进行堆叠规则构建')}
                </label>

                {bricks.length === 0 ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20 pointer-events-none">
                        <span className="material-icons text-4xl mb-2">layers</span>
                        <p className="text-xs uppercase font-bold tracking-tighter">Empty Rule</p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-3 items-center">
                        {bricks.map((brick, idx) => (
                            <div
                                key={idx}
                                draggable
                                onDragStart={() => setDraggedIndex(idx)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => draggedIndex !== null && moveBrick(draggedIndex, idx)}
                                className={`group relative flex items-center gap-2 bg-[#2d3748] border border-white/10 rounded-xl px-4 py-2 text-white shadow-lg transition-all ${draggedIndex === idx ? 'opacity-50 scale-95 border-primary' : 'hover:border-primary/50'
                                    }`}
                            >
                                {/* Grip */}
                                <span className="material-icons text-slate-500 text-sm cursor-grab active:cursor-grabbing">drag_indicator</span>

                                {/* Content based on type */}
                                <div className="flex flex-col">
                                    <span className="text-[9px] uppercase font-black text-primary/70 tracking-widest leading-none mb-1">
                                        {brick.type}
                                    </span>

                                    {brick.type === 'text' && (
                                        <input
                                            type="text"
                                            value={brick.value || ''}
                                            onChange={e => updateBrick(idx, { value: e.target.value.toUpperCase() })}
                                            className="bg-transparent border-none p-0 text-sm font-bold focus:ring-0 outline-none w-16"
                                        />
                                    )}

                                    {brick.type === 'date' && (
                                        <select
                                            value={brick.format || 'YYYYMMDD'}
                                            onChange={e => updateBrick(idx, { format: e.target.value })}
                                            className="bg-transparent border-none p-0 text-sm font-bold focus:ring-0 outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="YYYYMMDD">20250101</option>
                                            <option value="YYMMDD">250101</option>
                                            <option value="YYYYMM">202501</option>
                                            <option value="YYYY">2025</option>
                                        </select>
                                    )}

                                    {(brick.type === 'sequence' || brick.type === 'random') && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-[10px] text-slate-500">LEN:</span>
                                            <input
                                                type="number"
                                                min="1"
                                                max="12"
                                                value={brick.length || 4}
                                                onChange={e => updateBrick(idx, { length: parseInt(e.target.value) || 0 })}
                                                className="bg-transparent border-none p-0 text-sm font-bold focus:ring-0 outline-none w-8"
                                            />
                                        </div>
                                    )}

                                    {brick.type === 'separator' && (
                                        <select
                                            value={brick.value || '-'}
                                            onChange={e => updateBrick(idx, { value: e.target.value })}
                                            className="bg-transparent border-none p-0 text-sm font-bold focus:ring-0 outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="-">-</option>
                                            <option value="/">/</option>
                                            <option value=".">.</option>
                                            <option value="_">_</option>
                                            <option value=" ">Space</option>
                                        </select>
                                    )}

                                    {(brick.type === 'staff' || brick.type === 'company') && (
                                        <span className="text-xs font-mono text-slate-400 italic">AUTO</span>
                                    )}
                                </div>

                                {/* Remove Button */}
                                <button
                                    onClick={() => removeBrick(idx)}
                                    className="ml-2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
                                >
                                    <span className="material-icons text-sm">close</span>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BricksEditor;
